import React, {useMemo} from 'react';
import {observer} from 'mobx-react-lite';
import {getPrefixedLogger} from '@netx/core-next/instance/logger';
import {useSingleFileUploadProvider} from '../../contexts/SingleFileUploadContext';
import {UploaderDropzoneProvider} from '../../contexts/UploaderDropzoneContext';
import {type SingleFileUploadFormValues} from '../../hooks/useSingleFileUploadForm';
import {UploadMessage} from '../../locales';
import {fileMimeToFileType, getUnchunkedMaxFileSize} from '../../utils';
import {
	UploadSingleFileDialog,
	type UploadSingleFileDialogProps,
} from './UploadSingleFileDialog';

const logger = getPrefixedLogger('UploadSingleFileDialogContainer');

export interface UploadSingleFileDialogContainerProps<
	TFieldValues extends SingleFileUploadFormValues = SingleFileUploadFormValues,
> extends Omit<
	UploadSingleFileDialogProps<TFieldValues>,
	| 'file'
	| 'progress'
	| 'uploadComplete'
	| 'uploadStarted'
	| 'onAddFile'
	| 'onRemoveFile'
> {
	fileTypes?: string[];
	maxSize?: number;
	component?: React.ComponentType<UploadSingleFileDialogProps<TFieldValues>>;
}

export const UploadSingleFileDialogContainerPrimitive = <
	TFieldValues extends SingleFileUploadFormValues = SingleFileUploadFormValues,
>({
	fileTypes,
	maxSize: _maxSize,
	component,
	...props
}: UploadSingleFileDialogContainerProps<TFieldValues>) => {
	const uploadProvider = useSingleFileUploadProvider();

	const Component = component ?? UploadSingleFileDialog;

	const maxSize = _maxSize ?? getUnchunkedMaxFileSize();

	const defaultLimitationText = useMemo(
		() =>
			fileTypes
				? UploadMessage.SINGLE_FILE_UPLOAD_LIMITATION(
						fileTypes.map(fileMimeToFileType),
						maxSize,
					)
				: undefined,
		[fileTypes, maxSize],
	);

	const file = uploadProvider.uppyStore.pendingFiles[0];

	// TODO: when not using golden retriever we do not get a `uploadStarted` flag - it just remains null
	const isUploading = !!Object.values(
		uploadProvider.uppyStore.state.currentUploads,
	).length;

	return (
		<UploaderDropzoneProvider
			accept={fileTypes}
			disabled={uploadProvider.uppyStore.pendingFiles.length > 0}
			maxFiles={1}
			maxSize={maxSize ?? getUnchunkedMaxFileSize()}
			onDrop={(acceptedFiles, rejectedFiles) => {
				if (acceptedFiles.length)
					logger.debug('Files dropped', JSON.stringify(acceptedFiles));
				if (rejectedFiles.length)
					logger.debug('Files rejected', JSON.stringify(rejectedFiles));
				uploadProvider.addFiles(acceptedFiles);
			}}
		>
			<Component<TFieldValues>
				fileLimitationText={defaultLimitationText}
				{...props}
				file={file}
				progress={file?.progress?.percentage ?? 0}
				uploadComplete={file?.progress?.uploadComplete === true}
				uploadStarted={
					file?.progress?.uploadStarted != null ||
					(isUploading && file?.progress?.uploadComplete !== true)
				}
				onAddFile={uploadProvider.addFiles}
				onCancelFilePreview={() => uploadProvider.cancelFilePreview(file)}
				onRemoveFile={() => uploadProvider.removeAllFiles()}
				onRequestFilePreview={() => uploadProvider.requestFilePreview(file)}
			/>
		</UploaderDropzoneProvider>
	);
};

export const UploadSingleFileDialogContainer = observer(
	UploadSingleFileDialogContainerPrimitive,
);
